import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AirflowXcomEntry } from '../../types';

const XCOM_LIMIT = 50;

/*
 * Manages the XCom tab: paginated entry list, and the add/edit/delete CRUD flow
 */
export function useXcomTab(dagId: string, runId: string, taskId: string, mapIndex: number, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [entries, setEntries] = useState<AirflowXcomEntry[]>([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [offset, setOffset] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [editingEntry, setEditingEntry] = useState<AirflowXcomEntry | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [draftKey, setDraftKey] = useState('');
    const [draftValue, setDraftValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchEntries = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await AirflowDashboardHandler.listXcomEntries(Env, dagId, runId, taskId, { limit: XCOM_LIMIT, offset, mapIndex });
            setEntries(response.xcom_entries);
            setTotalEntries(response.total_entries);
        } catch {
            errorAlert('Failed to load XCom entries.');
        } finally {
            setIsLoading(false);
        }
    }, [Env, dagId, runId, taskId, offset, mapIndex]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries, refreshKey]);

    /*
     * Opens the Add XCom dialog with empty fields
     */
    const handleOpenAdd = useCallback(() => {
        setEditingEntry(null);
        setDraftKey('');
        setDraftValue('');
        setIsDialogOpen(true);
    }, []);

    /*
     * Opens the Edit XCom dialog pre-filled with the entry's current value
     */
    const handleOpenEdit = useCallback((entry: AirflowXcomEntry) => {
        setEditingEntry(entry);
        setDraftKey(entry.key);
        // The list endpoint never returns `value` (Airflow only includes it when reading a
        // single entry) — JSON.stringify(undefined) returns the literal value `undefined`,
        // not a string, so this must fall back to '' rather than pass that through.
        if (typeof entry.value === 'string') setDraftValue(entry.value);
        else setDraftValue(entry.value === undefined ? '' : JSON.stringify(entry.value, null, 2));
        setIsDialogOpen(true);
    }, []);

    /*
     * Creates a new entry or updates the value of the entry being edited
     */
    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);
        try {
            if (editingEntry) {
                await AirflowDashboardHandler.updateXcomEntry(Env, dagId, runId, taskId, editingEntry.key, { value: draftValue });
                successAlert(`Updated XCom entry "${editingEntry.key}".`);
            } else {
                await AirflowDashboardHandler.createXcomEntry(Env, dagId, runId, taskId, { key: draftKey, value: draftValue });
                successAlert(`Created XCom entry "${draftKey}".`);
            }
            setIsDialogOpen(false);
            fetchEntries();
        } catch {
            errorAlert('Failed to save XCom entry.');
        } finally {
            setIsSubmitting(false);
        }
    }, [Env, dagId, runId, taskId, editingEntry, draftKey, draftValue, fetchEntries]);

    /*
     * Confirms and deletes an XCom entry
     */
    const handleDeleteRequest = useCallback(
        (entry: AirflowXcomEntry) => {
            Modal.confirm({
                className: 'airflowDashboardRoot',
                title: 'Delete XCom Entry?',
                content: `Are you sure you want to delete XCom entry "${entry.key}"?`,
                okText: 'Yes, Delete',
                okButtonProps: { style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } },
                onOk: async () => {
                    try {
                        await AirflowDashboardHandler.deleteXcomEntry(Env, dagId, runId, taskId, entry.key, entry.map_index ?? -1);
                        successAlert(`Deleted XCom entry "${entry.key}".`);
                        fetchEntries();
                    } catch {
                        errorAlert(`Failed to delete XCom entry "${entry.key}".`);
                    }
                },
            });
        },
        [Env, dagId, runId, taskId, fetchEntries]
    );

    /*
     * Copies an entry's raw value to the clipboard
     */
    const handleCopyValue = useCallback((entry: AirflowXcomEntry) => {
        if (entry.value === undefined) {
            errorAlert('This value is not included in the list response — nothing to copy.');
            return;
        }
        const text = typeof entry.value === 'string' ? entry.value : JSON.stringify(entry.value);
        navigator.clipboard.writeText(text);
        successAlert('Copied value to clipboard.');
    }, []);

    const handlePrevPage = useCallback(() => setOffset((prev) => Math.max(0, prev - XCOM_LIMIT)), []);
    const handleNextPage = useCallback(() => setOffset((prev) => prev + XCOM_LIMIT), []);

    return {
        entries,
        totalEntries,
        offset,
        limit: XCOM_LIMIT,
        isLoading,
        editingEntry,
        isDialogOpen,
        setIsDialogOpen,
        draftKey,
        setDraftKey,
        draftValue,
        setDraftValue,
        isSubmitting,
        handleOpenAdd,
        handleOpenEdit,
        handleSubmit,
        handleDeleteRequest,
        handleCopyValue,
        handlePrevPage,
        handleNextPage,
    };
}
